import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Heart, Globe, ChevronDown, Menu, X } from "lucide-react";

const G      = "#3A9D23";
const G_DARK = "#2f7d1c";

const NAV_LINKS = [
  { label: "Hébergements", to: "/hotels"       },
  { label: "Destinations",  to: "/destinations" },
  { label: "À propos",      to: "/about"        },
  { label: "Contact",       to: "/contact"      },
];

function KomorotelLogo({ size = 36, light = false }) {
  const c = light ? "#fff" : G;
  return (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none">
      <circle cx="40" cy="40" r="38" fill={light ? "rgba(255,255,255,0.15)" : `${G}18`} />
      <path d="M22 55 Q15 40 22 27 Q30 15 42 14" stroke={c} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M22 55 Q28 63 38 64"              stroke={c} strokeWidth="3.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q50 68 54 57"              stroke={c} strokeWidth="2.5" strokeLinecap="round" fill="none"/>
      <path d="M38 64 Q35 58 40 54 Q45 50 42 44" stroke={c} strokeWidth="2"   strokeLinecap="round" fill="none"/>
      <rect x="39" y="12" width="7" height="48" rx="1.5" fill={c}/>
      <path d="M46 40 L64 24 L64 27 L52 40 L64 56 L61 56 L46 42 Z" fill={c}/>
      <circle cx="28" cy="28" r="2.5" fill={c}/>
      <circle cx="25" cy="36" r="2.5" fill={c}/>
      <circle cx="24" cy="44" r="2.5" fill={c}/>
      <circle cx="25" cy="52" r="2.5" fill={c}/>
    </svg>
  );
}

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled,   setScrolled]   = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const isHome      = location.pathname === "/";
  const transparent = isHome && !scrolled;

  const bg        = transparent ? "transparent" : "#fff";
  const shadow    = transparent ? "none"        : "0 1px 6px rgba(0,0,0,0.06)";
  const border    = transparent ? "transparent" : "#f3f4f6";
  const logoC     = transparent ? "#fff"                   : G;
  const logoSub   = transparent ? "rgba(255,255,255,0.7)"  : "#9ca3af";
  const linkC     = transparent ? "rgba(255,255,255,0.92)" : "#4b5563";
  const linkHover = transparent ? "#fff"                   : G;
  const iconC     = transparent ? "rgba(255,255,255,0.85)" : "#6b7280";
  const langBd    = transparent ? "rgba(255,255,255,0.35)" : "#e5e7eb";

  return (
    <>
      <style>{`
        .nb-nav   { display: flex; }
        .nb-right { display: flex; }
        .nb-burger { display: none; }
        @media (max-width: 900px) {
          .nb-nav    { display: none !important; }
          .nb-right  { display: none !important; }
          .nb-burger { display: flex !important; }
        }
      `}</style>

      <header style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
        background: bg,
        borderBottom: `1px solid ${border}`,
        boxShadow: shadow,
        transition: "background 0.3s, box-shadow 0.3s, border-color 0.3s",
      }}>
        <div style={{
          maxWidth: 1280, margin: "0 auto", padding: "0 24px",
          height: 64, display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 16,
        }}>

          {/* Logo */}
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none", flexShrink: 0 }}>
            <KomorotelLogo size={36} light={transparent} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, letterSpacing: "0.05em", color: logoC, lineHeight: 1.1, transition: "color 0.3s" }}>
                KOMOROTEL
              </div>
              <div style={{ fontSize: 10, color: logoSub, letterSpacing: "0.03em", marginTop: 1, transition: "color 0.3s" }}>
                L'archipel, votre destination.
              </div>
            </div>
          </Link>

          {/* Nav desktop */}
          <nav className="nb-nav" style={{ alignItems: "center", gap: 6, flex: 1, justifyContent: "center" }}>
            {NAV_LINKS.map(l => (
              <Link key={l.to} to={l.to}
                style={{ textDecoration: "none", fontSize: 13, fontWeight: 500, color: linkC, transition: "color 0.2s", padding: "6px 10px", borderRadius: 8, whiteSpace: "nowrap" }}
                onMouseEnter={e => e.currentTarget.style.color = linkHover}
                onMouseLeave={e => e.currentTarget.style.color = linkC}>
                {l.label}
              </Link>
            ))}
          </nav>

          {/* Right */}
          <div className="nb-right" style={{ alignItems: "center", gap: 8, flexShrink: 0 }}>
            <button style={{ display: "flex", alignItems: "center", background: "none", border: "none", cursor: "pointer", color: iconC, padding: "6px 8px", borderRadius: 8, fontSize: 13, transition: "color 0.3s", whiteSpace: "nowrap" }}>
              <Heart size={15} /><span style={{ marginLeft: 4 }}>Favoris</span>
            </button>
            <button style={{ display: "flex", alignItems: "center", background: "none", border: `1px solid ${langBd}`, borderRadius: 7, cursor: "pointer", color: iconC, padding: "5px 8px", gap: 3, transition: "all 0.3s" }}>
              <Globe size={14} /><span style={{ margin: "0 2px", fontSize: 13 }}>FR</span><ChevronDown size={12} />
            </button>
            <button onClick={() => navigate("/login")}
              style={{ background: G, color: "#fff", border: "none", borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 700, cursor: "pointer", transition: "background 0.18s", whiteSpace: "nowrap" }}
              onMouseEnter={e => e.currentTarget.style.backgroundColor = G_DARK}
              onMouseLeave={e => e.currentTarget.style.backgroundColor = G}>
              Se connecter
            </button>
          </div>

          {/* Burger */}
          <button className="nb-burger"
            onClick={() => setMobileOpen(true)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 6, alignItems: "center", justifyContent: "center" }}>
            <Menu size={22} color={transparent ? "#fff" : "#374151"} />
          </button>
        </div>

        {/* Mobile overlay */}
        {mobileOpen && (
          <div style={{ position: "fixed", inset: 0, background: "#fff", zIndex: 999, display: "flex", flexDirection: "column" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 24px", borderBottom: "1px solid #f3f4f6" }}>
              <Link to="/" style={{ display: "flex", alignItems: "center", gap: 9, textDecoration: "none" }} onClick={() => setMobileOpen(false)}>
                <KomorotelLogo size={32} />
                <div style={{ fontWeight: 800, fontSize: 14, color: G }}>KOMOROTEL</div>
              </Link>
              <button style={{ background: "none", border: "none", cursor: "pointer" }} onClick={() => setMobileOpen(false)}>
                <X size={22} color="#374151" />
              </button>
            </div>
            <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 4, overflowY: "auto" }}>
              {NAV_LINKS.map(l => (
                <Link key={l.to} to={l.to}
                  style={{ textDecoration: "none", fontSize: 15, fontWeight: 500, color: "#1f2937", padding: "12px 16px", borderRadius: 10, display: "block" }}
                  onClick={() => setMobileOpen(false)}>
                  {l.label}
                </Link>
              ))}
              <div style={{ borderTop: "1px solid #f3f4f6", marginTop: 12, paddingTop: 12 }}>
                <button
                  style={{ background: G, color: "#fff", border: "none", borderRadius: 9, padding: "12px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer", width: "100%" }}
                  onClick={() => { navigate("/login"); setMobileOpen(false); }}>
                  Se connecter
                </button>
              </div>
            </div>
          </div>
        )}
      </header>
    </>
  );
}